import { clear, createStore, delMany, getMany, keys, set } from 'idb-keyval';

import StorageService, { CacheObject, IStorage } from './StorageService';
import { notUndefined } from './utils';

export type CacheKeyType = string | number | undefined;
// Created lazily: idb-keyval's createStore() calls indexedDB.open() immediately,
// which throws a SecurityError synchronously when storage is denied (sandboxed
// iframes, blocked site data). At module level that would crash the whole app
// on import; lazily, it only fails the (already try/catch'd) storage calls.
let idbStore: ReturnType<typeof createStore> | undefined;
const getStore = () => (idbStore ??= createStore('curipod-db', 'files-store'));

class IndexedDBStorageService extends StorageService implements IStorage {
  /**
   *
   * @param key cache key
   * @param data can be any data
   * @param options options for staletime
   * @returns
   */
  public async save<T>(
    key: CacheKeyType[],
    data: T,
    options: { staleTime: number } = { staleTime: 1000 * 60 * 60 * 8 },
  ) {
    try {
      const saveData = this.createCacheObjectToSave(data, options.staleTime);
      await set(this.makeKey(key), saveData, getStore());
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error saving file', e);
    }
  }
  public async saveMany<T>(
    data: {
      key: CacheKeyType[];
      data: T;
      options: { staleTime: number };
    }[],
  ) {
    try {
      await Promise.all(
        data.map(async ({ key, data, options }) => {
          await this.save(key, data, options);
        }),
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error saving file', e);
    }
  }

  private async retrieveMany<T>(keys: CacheKeyType[][]) {
    const ids = keys.map(this.makeKey);
    const cacheObjects = await getMany<CacheObject<T>>(ids, getStore());
    if (!cacheObjects) return undefined;
    const result = cacheObjects.filter(notUndefined).reduce<{
      loadedData: T[];
      staleKeys: CacheKeyType[][];
    }>(
      (acc, cacheObject, index) => {
        if (cacheObject.cacheTime + cacheObject.staleTime > Date.now()) {
          return {
            ...acc,
            loadedData: [...acc.loadedData, cacheObject.payload],
          };
        } else {
          const key = keys[index];
          return {
            ...acc,
            staleKeys: [...acc.staleKeys, key],
          };
        }
      },
      { loadedData: [], staleKeys: [] },
    );

    return result;
  }

  public async retrieveManyAndDeleteStale<T>(keys: CacheKeyType[][]) {
    let result;
    try {
      result = await this.retrieveMany<T>(keys);
    } catch (e) {
      // Storage unavailable — behave as an empty cache.
      // eslint-disable-next-line no-console
      console.error('Error reading IndexedDB cache', e);
      return undefined;
    }
    if (!result) return undefined;

    // Delete all stale data
    await this.deleteMany(result.staleKeys);
    return result.loadedData;
  }

  public async deleteMany(keys: CacheKeyType[][]) {
    try {
      await delMany(keys.map(this.makeKey), getStore());
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error deleting from IndexedDB cache', e);
    }
  }

  /**
   * Clears all cache that is stale.
   */
  public async clearStaleCache() {
    try {
      const allKeys = await keys(getStore());
      const keyStrings = allKeys.map((key) =>
        this.makeKeyFromString(key.toString()),
      );
      await this.retrieveManyAndDeleteStale(keyStrings);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error clearing stale IndexedDB cache', e);
    }
  }

  /**
   * Wipes the entire curipod-db file store. The store is Curipod-owned by
   * definition, so this is in-scope for the Curipod-embed fresh-start.
   */
  public async clearAll() {
    try {
      await clear(getStore());
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error clearing IndexedDB store', e);
    }
  }
}

export default new IndexedDBStorageService();
